import React from 'react';
import { ArrowRightIcon, CheckIcon, FileTextIcon } from 'lucide-react';
import { KnowledgeMap } from './KnowledgeMap';
import { StatusPill } from '../ui/StatusPill';
import { evidence, samplePassage, sampleRelationship } from '../../data/sampleResearch';
import type { WorkflowStepId } from '../../data/researchWorkflow';
export function WorkflowStepScene({
  step
}: {
  step: WorkflowStepId;
}) {
  switch (step) {
    case 'source':
      return <SourceScene data-id="element-343" data-mp="33m1ka6rrt09v" />;
    case 'passage':
      return <PassageScene data-id="element-344" data-mp="38mz2gjcs5ubd" />;
    case 'relationship':
      return <RelationshipScene data-id="element-345" data-mp="38nqvbmeztdxy" />;
    case 'approve':
      return <ApproveScene data-id="element-346" data-mp="2ung7ytcw6hhj" />;
    default:
      return <MapScene data-id="element-347" data-mp="2uohld4hdrg2f" />;
  }
}
function SourceScene() {
  const sections = [{
    name: 'Abstract',
    linked: 0
  }, {
    name: 'Methods',
    linked: 0
  }, {
    name: 'Results',
    linked: 2
  }, {
    name: 'Discussion',
    linked: 1
  }];
  return <div className="grid h-full gap-4 sm:grid-cols-[1fr_200px]" data-id="element-348" data-mp="wwn0l8zetf85">
      <div className="rounded-2xl bg-white p-5 ring-1 ring-ink/[0.05]" data-id="element-349" data-mp="wuspemdxx1h9">
        <div className="flex items-center gap-2 text-xs text-ink-muted" data-id="element-350" data-mp="187m59m2jhgha">
          <FileTextIcon className="h-4 w-4" aria-hidden="true" data-id="element-351" data-mp="13adjzcm3gwbt" /> Peer-reviewed article
        </div>
        <h3 className="mt-3 text-lg font-semibold leading-snug tracking-tight" data-id="element-352" data-mp="1hz00hhngju4m">
          Sleep fragmentation and working memory in adolescents
        </h3>
        <p className="mt-1 text-sm text-ink-muted" data-id="element-353" data-mp="1d1qbu47w7mhu">Hendricks, Abara, Lund · 2023</p>
        <dl className="mt-5 grid grid-cols-2 gap-x-4 gap-y-3 text-[13px]" data-id="element-354" data-mp="pab9ibg4956l">
          {[['Participants', '312 (sample)'], ['Design', 'Longitudinal'], ['Concepts', '9 linked'], ['Library', 'Cognitive Science']].map(([k, v]) => <div key={k} data-id="element-355" data-mp="u7kvti87l0q7">
              <dt className="text-[11px] text-ink-muted" data-id="element-356" data-mp="ubigw9eck2cq">{k}</dt>
              <dd className="font-medium" data-id="element-357" data-mp="2am2zsbzyk8vx">{v}</dd>
            </div>)}
        </dl>
      </div>
      <div className="rounded-2xl bg-white/60 p-4 ring-1 ring-ink/[0.05]" data-id="element-358" data-mp="25nszjmzdmi3g">
        <p className="text-xs font-medium text-ink-muted" data-id="element-359" data-mp="2csap8lj6wo7o">Sections</p>
        <ul className="mt-3 space-y-1" data-id="element-360" data-mp="2hqc4swheq5ee">
          {sections.map(s => <li key={s.name} className={`flex items-center justify-between rounded-lg px-2.5 py-2 text-[13px] ${s.name === 'Results' ? 'bg-white font-medium shadow-sm' : 'text-ink-soft'}`} data-id="element-361" data-mp="2hsovby18do32">
              {s.name}
              {s.linked ? <span className="rounded-full bg-accent-soft px-1.5 text-[11px] font-medium text-accent-ink" data-id="element-362" data-mp="1u2cdnvg6ocza">{s.linked}</span> : null}
            </li>)}
        </ul>
      </div>
    </div>;
}
function PassageScene() {
  return <div className="relative h-full rounded-2xl bg-white p-6 ring-1 ring-ink/[0.05]" data-id="element-363" data-mp="23xcquyx9wj8y">
      <p className="text-xs text-ink-muted" data-id="element-364" data-mp="28s0hb3bu7nq6">{samplePassage.location}</p>
      <p className="mt-4 max-w-lg text-[15px] leading-[1.8] text-ink-soft" data-id="element-365" data-mp="1b9qplxbipqp9">
        Sleep was recorded with wrist actigraphy across fourteen nights. {samplePassage.before}
        <mark className="mark-evidence" data-id="element-366" data-mp="3tkedgdpa91j3">{samplePassage.highlight}</mark>
        {samplePassage.after} Effects were smaller among participants with consistent weekend schedules.
      </p>
      <div className="glass absolute bottom-5 right-5 w-60 rounded-2xl p-3.5 shadow-glass" data-id="element-367" data-mp="3kiy6yh1l9jk5">
        <p className="text-[11px] font-medium text-ink-muted" data-id="element-368" data-mp="3fk3lt8g5fiwv">Linked to</p>
        <p className="mt-1 text-[13px] font-medium" data-id="element-369" data-mp="3fm7oxo684dw3">Sleep fragmentation → Working memory</p>
        <p className="mt-1 text-[11px] text-ink-muted" data-id="element-370" data-mp="3ambofywqj4la">Passage added by R. Lindqvist</p>
      </div>
    </div>;
}
function RelationshipScene() {
  return <div className="flex h-full flex-col rounded-2xl bg-white p-6 ring-1 ring-ink/[0.05]" data-id="element-371" data-mp="31rxb41vrm44t">
      <div className="flex items-center justify-between" data-id="element-372" data-mp="2wuewpvyzawpf">
        <p className="text-xs font-medium text-ink-muted" data-id="element-373" data-mp="2wrspxyjwwl89">Proposed relationship</p>
        <StatusPill tone="warn" data-id="element-374" data-mp="2rtr63benwhvq">Awaiting review</StatusPill>
      </div>
      <div className="mt-6 flex flex-col items-center gap-3 sm:flex-row sm:justify-center" data-id="element-375" data-mp="2mwhafe0oj9zg">
        <span className="rounded-xl bg-ink px-3.5 py-2 text-sm font-medium text-white" data-id="element-376" data-mp="qtcjdzhhevpy">{sampleRelationship.from}</span>
        <span className="flex items-center gap-1.5 rounded-full bg-accent-soft px-3 py-1 text-xs font-medium text-accent-ink shadow-glow" data-id="element-377" data-mp="vquv1xmvvp1n">
          {sampleRelationship.relation} <ArrowRightIcon className="h-3 w-3" aria-hidden="true" data-id="element-378" data-mp="m0t6l4qmc4m7" />
        </span>
        <span className="rounded-xl bg-ink px-3.5 py-2 text-sm font-medium text-white" data-id="element-379" data-mp="8239hp3su4xu">{sampleRelationship.to}</span>
      </div>
      <table className="mt-8 w-full text-left text-[13px]" data-id="element-380" data-mp="d1xz7pbu8ohb">
        <thead data-id="element-381" data-mp="3u91jeoawncgs">
          <tr className="text-[11px] text-ink-muted" data-id="element-382" data-mp="3u9jma3s2ooxo">
            <th className="pb-2 font-medium" data-id="element-383" data-mp="3uautkdkq8etj">Source</th>
            <th className="hidden pb-2 font-medium sm:table-cell" data-id="element-384" data-mp="31xeq028g2bj">Location</th>
            <th className="pb-2 text-right font-medium" data-id="element-385" data-mp="1shwtbwomejyk">Stance</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-ink/[0.06]" data-id="element-386" data-mp="1xghubybp57o2">
          {evidence.map(e => <tr key={e.source} data-id="element-387" data-mp="3hysdhqx6yxpr">
              <td className="py-2.5 font-medium" data-id="element-388" data-mp="3hy0oiyy3ny8g">{e.source}</td>
              <td className="hidden py-2.5 text-ink-muted sm:table-cell" data-id="element-389" data-mp="3czpiz0eu89w1">{e.detail}</td>
              <td className="py-2.5 text-right" data-id="element-390" data-mp="3cxuujc4cxzn7">
                <StatusPill tone={e.tone} data-id="element-391" data-mp="3rpna9p82dfpg">{e.stance}</StatusPill>
              </td>
            </tr>)}
        </tbody>
      </table>
      <p className="mt-auto pt-4 text-[12px] text-ink-muted" data-id="element-392" data-mp="3mvshzxee7ser">Confidence: {sampleRelationship.confidence} · proposed by curation pipeline</p>
    </div>;
}
function ApproveScene() {
  const trail = [{
    label: 'Proposed from Hendricks et al., 2023',
    who: 'Curation pipeline',
    time: '09:02'
  }, {
    label: 'Passage linked and checked',
    who: 'R. Lindqvist',
    time: '09:40'
  }, {
    label: 'Approved as finding · version 14',
    who: 'A. Osei',
    time: '10:15'
  }];
  return <div className="flex h-full flex-col rounded-2xl bg-white p-6 shadow-glow-ok ring-1 ring-ok/20" data-id="element-393" data-mp="2g9yo302mwkpp">
      <div className="flex items-center gap-2.5" data-id="element-394" data-mp="2bbwsxgrxouxv">
        <span className="flex h-7 w-7 items-center justify-center rounded-full bg-ok text-white" data-id="element-395" data-mp="f7h0ua6odn0y">
          <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-396" data-mp="k60m08dg2us9" />
        </span>
        <div data-id="element-397" data-mp="k850fk8yzc6s">
          <p className="text-sm font-semibold" data-id="element-398" data-mp="o7vq1kmrfm9q">Finding approved</p>
          <p className="text-[12px] text-ink-muted" data-id="element-399" data-mp="o6kkbf9r84fz">Sleep fragmentation is associated with reduced working memory</p>
        </div>
      </div>
      <p className="mt-6 text-xs font-medium text-ink-muted" data-id="element-400" data-mp="y6bibqxix1kt">Provenance</p>
      <ol className="mt-3 space-y-0" data-id="element-401" data-mp="y48pz08wewcx">
        {trail.map((t, i) => <li key={t.label} className="relative flex gap-3 pb-4 last:pb-0" data-id="element-402" data-mp="133ldln7lg7gk" data-mpi={i}>
            {i < trail.length - 1 ? <span className="absolute left-[5px] top-3 h-full w-px bg-ink/10" aria-hidden="true" data-id="element-403" data-mp="17wef827rmzof" /> : null}
            <span className={`relative mt-1.5 h-2.5 w-2.5 shrink-0 rounded-full ${i === trail.length - 1 ? 'bg-ok' : 'bg-ink/20'}`} data-id="element-404" data-mp="1bwoktdrnbo2r" />
            <div className="flex flex-1 justify-between gap-3 text-[13px]" data-id="element-405" data-mp="1lve5tfeu9on8">
              <span data-id="element-406" data-mp="1ltb6g4rsqj5u">
                <span className="font-medium" data-id="element-407" data-mp="1vzb3cqyrafch">{t.label}</span>
                <span className="block text-[12px] text-ink-muted" data-id="element-408" data-mp="1w0vxwgl2lyac">{t.who}</span>
              </span>
              <span className="text-[12px] tabular-nums text-ink-faint" data-id="element-409" data-mp="1qz6mv0xulkm9">{t.time}</span>
            </div>
          </li>)}
      </ol>
    </div>;
}
function MapScene() {
  return <div className="relative h-full rounded-2xl bg-white p-4 ring-1 ring-ink/[0.05]" data-id="element-410" data-mp="1i1e4fnwtxwun">
      <KnowledgeMap showNew className="h-full w-full" data-id="element-411" data-mp="2eor5tju04hvj" />
      <div className="glass absolute left-4 top-4 rounded-xl px-3 py-2 shadow-glass" data-id="element-412" data-mp="29r95ghu1vaz5">
        <p className="text-[11px] text-ink-muted" data-id="element-413" data-mp="25098jckxkswn">Knowledge map · v14</p>
        <p className="text-[13px] font-medium" data-id="element-414" data-mp="24yg1hw6eo6o4">1 new reviewed relationship</p>
      </div>
    </div>;
}